import { useRef } from "react";
import { Button, Spinner } from "@shopify/polaris";
import "./FieldCapture.css";

// Shared pieces of the public phone pages (provider service capture and
// trasladista expense tickets): a token-authenticated edge-function caller,
// client-side photo compression, the camera/gallery button and the thumbnail.

export function makeCaptureClient(functionName) {
  const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${functionName}`;
  const headers = { apikey: process.env.REACT_APP_SUPABASE_ANON_KEY };
  return async function call(body) {
    const isForm = body instanceof FormData;
    const res = await fetch(url, {
      method: "POST",
      headers: isForm ? headers : { ...headers, "Content-Type": "application/json" },
      body: isForm ? body : JSON.stringify(body),
    });
    let data = null;
    try {
      data = await res.json();
    } catch {
      /* non-JSON error page */
    }
    if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);
    return data;
  };
}

// Phone photos are 3–8 MB; shrink before uploading over mobile data. Also
// turns HEIC (iPhone) into JPEG, which is what the backend and Claude expect.
export async function compressImage(file, maxDim = 1600, quality = 0.82) {
  try {
    let source;
    let w;
    let h;
    if (window.createImageBitmap) {
      source = await createImageBitmap(file, { imageOrientation: "from-image" });
      w = source.width;
      h = source.height;
    } else {
      source = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
      w = source.naturalWidth;
      h = source.naturalHeight;
    }
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
    return blob || file;
  } catch {
    return file;
  }
}

export function PickPhoto({ onFiles, camera = true, multiple = false, children, ...btn }) {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture={camera ? "environment" : undefined}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          if (files.length) onFiles(files);
        }}
      />
      <Button {...btn} onClick={() => ref.current?.click()}>
        {children}
      </Button>
    </>
  );
}

export function Thumb({ photo, onDelete, disabled }) {
  return (
    <div className="fc-thumb">
      <img src={photo.url || photo.preview} alt={photo.label || photo.kind} />
      {photo.uploading && (
        <div className="fc-thumb-overlay">
          <Spinner size="small" />
        </div>
      )}
      {photo.failed && (
        <div className="fc-thumb-overlay fc-thumb-failed">Error</div>
      )}
      {onDelete && !photo.uploading && !disabled && (
        <button
          type="button"
          className="fc-thumb-delete"
          aria-label="Eliminar foto"
          onClick={() => onDelete(photo)}
        >
          ✕
        </button>
      )}
    </div>
  );
}
